import { Space, Button, Popconfirm, InputNumber, Input, Tag, Tooltip } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PrinterOutlined,
  NumberOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

import DocLink from "../../../components/table/DocLink";
const num = (v) => Number(v || 0);
const money = (v, d = 2) =>
  num(v).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });

/* เลข Invoice ที่คู่กับ PI ใบนี้ — เลขรัน 4 หลักตัวเดียวกัน */
export const invCodeOf = (runno) =>
  runno ? `NSF${String(runno).padStart(4, "0")}` : "";

/* ใบร่างยังไม่มีเลขเอกสาร — ใช้คีย์ภายในขึ้นต้นด้วย DFT */
export const isDraft = (r) =>
  Number(r?.is_draft) === 1 || String(r?.pi_code || "").startsWith("DFT");

export const piCodeLabel = (r) => (isDraft(r) ? "ยังไม่ออกเลข" : r?.pi_code || "-");

export const proformaHeader = {
  pi_code: null,
  source_type: "MANUAL",
  inv_code: null,
  pi_date: dayjs(),
  cuscode: null,
  cusname: null,
  brand: null,
  currency: "USD",
  customer_po: null,
  country_of_origin: "THAILAND",
  ship_via: null,
  delivery_term: null,
  payment_term: null,
  consignee_id: null,
  consignee_name: null,
  consignee_address: null,
  consignee_contact: null,
  consignee_email: null,
  consignee_tel: null,
  notify_party: null,
  port_of_loading: null,
  place_of_receipt: null,
  final_destination: null,
  carrier: null,
  fob_term: null,
  etd: null,
  eta: null,
  net_weight: null,
  gross_weight: null,
  volume_cbm: null,
  bank_beneficiary: null,
  bank_name: null,
  bank_branch: null,
  bank_address: null,
  bank_account: null,
  swift_code: null,
  remark: null,
};

export const emptyDetailRow = (seq) => ({
  _rid: `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  seq,
  pg_code: null,
  pg_name: "",
  hs_code: "",
  packing_size: "",
  net_wt: null,
  gross_wt: null,
  unit_cs: null,
  carton: null,
  total_qty: null,
  unit_price: null,
  line_total: 0,
  detail_remark: "",
});

export const accessColumn = ({ handleView, handleEdit, handleDelete, handlePrint, handleReserve }) => [
  {
    title: "เลขที่ PI",
    dataIndex: "pi_code",
    key: "pi_code",
    width: 130,
    render: (v, r) =>
      isDraft(r) ? (
        <Tooltip title="ยังไม่ได้ออกเลขเอกสาร — กดปุ่มออกเลขเมื่อพร้อม">
          <Tag color="default">ยังไม่ออกเลข</Tag>
        </Tooltip>
      ) : (
        /* ออกเลขแล้วจึงกดเปิดดูเอกสารได้ ใบร่างยังไม่มีเลขจึงไม่ทำเป็นลิงก์ */
        <DocLink value={v} record={r} onClick={handleEdit} title="เปิดดู Proforma Invoice" />
      ),
  },
  {
    title: "เลข Invoice",
    key: "inv_link",
    width: 160,
    render: (_, r) => {
      if (r.inv_code) return <Tag color="green">{r.inv_code}</Tag>;
      if (Number(r.inv_reserved) === 1) {
        return (
          <Tooltip title="จองเลขไว้แล้ว รอออก Invoice">
            <Tag color="gold">{invCodeOf(r.runno)} (จองไว้)</Tag>
          </Tooltip>
        );
      }
      return <Tag>ยังไม่ออกเลข</Tag>;
    },
  },
  { title: "ลูกค้า", dataIndex: "cusname", key: "cusname", width: 200 },
  { title: "Brand", dataIndex: "brand", key: "brand", width: 130 },
  {
    title: "วันที่",
    dataIndex: "pi_date",
    key: "pi_date",
    width: 110,
    align: "center",
    render: (v) => (v ? dayjs(v).format("DD/MM/YYYY") : "-"),
  },
  {
    title: "ที่มา",
    dataIndex: "source_type",
    key: "source_type",
    width: 110,
    align: "center",
    render: (v) =>
      v === "INVOICE" ? <Tag color="blue">จาก Invoice</Tag> : <Tag color="default">กรอกเอง</Tag>,
  },
  {
    title: "ยอดรวม",
    dataIndex: "total_amount",
    key: "total_amount",
    width: 130,
    align: "right",
    render: (v, r) => `${r.currency || ""} ${money(v)}`,
  },
  { title: "สร้างโดย", dataIndex: "created_name", key: "created_name", width: 140 },
  {
    title: "Action",
    key: "action",
    fixed: "right",
    width: 210,
    render: (_, record) => (
      <Space>
        <Button
          size="small"
          icon={<EyeOutlined />}
          className="bn-success-outline"
          title="ดูข้อมูล"
          onClick={() => handleView(record)}
        />
        <Tooltip title="แก้ไข">
          <Button
            size="small"
            icon={<EditOutlined />}
            className="bn-primary-outline"
            onClick={() => handleEdit(record)}
          />
        </Tooltip>
        <Tooltip
          title={
            record.inv_code
              ? `ออก Invoice แล้ว : ${record.inv_code}`
              : Number(record.inv_reserved) === 1
              ? "ยกเลิกการจองเลข Invoice"
              : isDraft(record)
              ? "ออกเลขเอกสาร + จองเลข Invoice"
              : "จองเลข Invoice"
          }
        >
          <Button
            size="small"
            icon={<NumberOutlined />}
            className={Number(record.inv_reserved) === 1 ? "bn-warning-outline" : "bn-secondary-outline"}
            disabled={!!record.inv_code}
            onClick={() => handleReserve(record)}
          />
        </Tooltip>
        <Tooltip title={isDraft(record) ? "ต้องออกเลขเอกสารก่อนถึงจะพิมพ์ได้" : "พิมพ์"}>
          <Button
            size="small"
            icon={<PrinterOutlined />}
            className="bn-warning-outline"
            disabled={isDraft(record)}
            onClick={() => handlePrint(record)}
          />
        </Tooltip>
        <Popconfirm
          title="ยืนยันการยกเลิกเอกสาร?"
          onConfirm={() => handleDelete(record)}
          okText="ยกเลิกเอกสาร"
          cancelText="ปิด"
          disabled={!!record.inv_code}
        >
          <Button size="small" danger icon={<DeleteOutlined />} disabled={!!record.inv_code} />
        </Popconfirm>
      </Space>
    ),
  },
];

/* ตารางสินค้า — PI กรอกชื่อสินค้าเองได้ ไม่บังคับรหัส */
export const detailColumns = ({ updateDetail, deleteDetail, currency, openPicker, readOnly = false }) => [
  { title: "ลำดับ", key: "seq", dataIndex: "seq", width: 55, align: "center" },
  {
    title: "รหัสสินค้า",
    key: "pg_code",
    width: 150,
    render: (_, record, index) => (
      <Space.Compact style={{ width: "100%" }}>
        <Input
          value={record.pg_code || ""}
          disabled={readOnly}
          placeholder="ไม่ต้องกรอกก็ได้"
          onChange={(e) => updateDetail(index, "pg_code", e.target.value)}
        />
        <Button disabled={readOnly} onClick={() => openPicker(index)}>เลือก</Button>
      </Space.Compact>
    ),
  },
  {
    title: "รายการสินค้า",
    key: "pg_name",
    render: (_, record, index) => (
      <Input
        value={record.pg_name || ""}
        disabled={readOnly}
        placeholder="พิมพ์ชื่อสินค้าได้เลย"
        onChange={(e) => updateDetail(index, "pg_name", e.target.value)}
      />
    ),
  },
  {
    title: "HS Code",
    key: "hs_code",
    width: 120,
    render: (_, record, index) => (
      <Input
        value={record.hs_code || ""}
        disabled={readOnly}
        onChange={(e) => updateDetail(index, "hs_code", e.target.value)}
      />
    ),
  },
  {
    title: "Packing Size",
    key: "packing_size",
    width: 130,
    render: (_, record, index) => (
      <Input
        value={record.packing_size || ""}
        disabled={readOnly}
        placeholder="เช่น 10x10x100G"
        onChange={(e) => updateDetail(index, "packing_size", e.target.value)}
      />
    ),
  },
  {
    title: "Pcs/Ctn",
    key: "unit_cs",
    width: 95,
    align: "right",
    render: (_, record, index) => (
      <InputNumber
        min={0}
        style={{ width: "100%" }}
        value={record.unit_cs}
        disabled={readOnly}
        onChange={(v) => updateDetail(index, "unit_cs", v)}
      />
    ),
  },
  {
    title: "กล่อง",
    key: "carton",
    width: 95,
    align: "right",
    render: (_, record, index) => (
      <InputNumber
        min={0}
        style={{ width: "100%" }}
        value={record.carton}
        disabled={readOnly}
        onChange={(v) => updateDetail(index, "carton", v)}
      />
    ),
  },
  {
    title: "จำนวนรวม",
    key: "total_qty",
    width: 110,
    align: "right",
    render: (_, record) => money(num(record.unit_cs) * num(record.carton), 0),
  },
  {
    title: `ราคา/หน่วย (${currency || "USD"})`,
    key: "unit_price",
    width: 130,
    align: "right",
    render: (_, record, index) => (
      <InputNumber
        min={0}
        style={{ width: "100%" }}
        value={record.unit_price}
        disabled={readOnly}
        onChange={(v) => updateDetail(index, "unit_price", v)}
      />
    ),
  },
  {
    title: `รวม (${currency || "USD"})`,
    key: "line_total",
    width: 130,
    align: "right",
    render: (_, record) => money(record.line_total),
  },
  ...(readOnly
    ? []
    : [
        {
          title: "",
          key: "op",
          width: 45,
          align: "center",
          render: (_, record, index) => (
            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => deleteDetail(index)} />
          ),
        },
      ]),
];
